declare global {
  interface Window {
    google: any;
  }
}

export interface GooglePlaceResult {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export interface GooglePlaceDetails {
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  place_id: string;
  types: string[];
}

export interface ParsedAddress {
  street_number?: string;
  route?: string;
  subpremise?: string;
  locality?: string;
  administrative_area_level_1?: string;
  postal_code?: string;
  country?: string;
  formatted_address: string;
  place_id: string;
}

export class GooglePlacesService {
  private static autocompleteService: any = null;
  private static placesService: any = null;
  private static geocoder: any = null;

  static initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        this.autocompleteService = new window.google.maps.places.AutocompleteService();
        this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
        this.geocoder = new window.google.maps.Geocoder();
        resolve();
      } else {
        // Wait for Google Maps to load
        const checkGoogleMaps = () => {
          if (window.google && window.google.maps) {
            this.autocompleteService = new window.google.maps.places.AutocompleteService();
            this.placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
            this.geocoder = new window.google.maps.Geocoder();
            resolve();
          } else {
            setTimeout(checkGoogleMaps, 100);
          }
        };
        checkGoogleMaps();
      }
    });
  }

  static async getPlacePredictions(input: string, country?: string): Promise<GooglePlaceResult[]> {
    await this.initialize();

    return new Promise((resolve, reject) => {
      if (!this.autocompleteService) {
        reject(new Error('Autocomplete service not initialized'));
        return;
      }

      const options: any = {
        input,
        types: ['address'],
        componentRestrictions: { country: country || 'us' },
      };

      this.autocompleteService.getPlacePredictions(options, (predictions: GooglePlaceResult[], status: string) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          resolve(predictions);
        } else {
          resolve([]);
        }
      });
    });
  }

  static async getPlaceDetails(placeId: string): Promise<GooglePlaceDetails> {
    await this.initialize();

    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.places?.Place) {
        reject(new Error('Place API not available'));
        return;
      }

      const place = new window.google.maps.places.Place({
        id: placeId,
        requestedLanguage: 'en',
        fields: ['addressComponents', 'formattedAddress', 'geometry', 'id', 'types'],
      });

      place
        .fetchFields()
        .then((result: any) => {
          // Map new field names to the expected GooglePlaceDetails interface
          const mappedResult: GooglePlaceDetails = {
            address_components: result.addressComponents || [],
            formatted_address: result.formattedAddress || '',
            geometry: result.geometry || { location: { lat: 0, lng: 0 } },
            place_id: result.id || placeId,
            types: result.types || [],
          };
          resolve(mappedResult);
        })
        .catch(reject);
    });
  }

  static parseAddressComponents(placeDetails: GooglePlaceDetails): ParsedAddress {
    const address: ParsedAddress = {
      formatted_address: placeDetails.formatted_address,
      place_id: placeDetails.place_id,
    };

    placeDetails.address_components.forEach((component) => {
      const types = component.types;

      if (types.includes('street_number')) {
        address.street_number = component.long_name;
      } else if (types.includes('route')) {
        address.route = component.long_name;
      } else if (types.includes('subpremise')) {
        address.subpremise = component.long_name;
      } else if (types.includes('locality')) {
        address.locality = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        address.administrative_area_level_1 = component.long_name;
      } else if (types.includes('postal_code')) {
        address.postal_code = component.long_name;
      } else if (types.includes('country')) {
        address.country = component.long_name;
      }
    });

    return address;
  }

  static async validateAddress(address: string, country?: string): Promise<boolean> {
    await this.initialize();

    return new Promise((resolve) => {
      if (!this.geocoder) {
        resolve(false);
        return;
      }

      const geocoderRequest = {
        address,
        componentRestrictions: country ? { country } : undefined,
      };

      this.geocoder.geocode(geocoderRequest, (results: any[], status: string) => {
        if (status === window.google.maps.GeocoderStatus.OK && results.length > 0) {
          // Check if the result is a valid address (not just a city or region)
          const result = results[0];
          const types = result.types;
          const validAddressTypes = ['premise', 'subpremise', 'establishment'];
          const isValidStreetAddress = types.includes('street_address');
          const isValidAddress = validAddressTypes.some((type) => types.includes(type));
          resolve(isValidStreetAddress || isValidAddress);
        } else {
          resolve(false);
        }
      });
    });
  }

  static async validatePostalCode(
    postalCode: string,
    country?: string,
  ): Promise<boolean> {
    await this.initialize();

    return new Promise((resolve) => {
      if (!this.geocoder) return resolve(false);

      /* AU + 2000 is Google’s sample; we do the same pattern */
      const request = {
        componentRestrictions: {
          ...(country ? { country } : {}),
          postalCode,
        },
      };

      this.geocoder.geocode(request, (results: any[], status: string) => {
        resolve(status === window.google.maps.GeocoderStatus.OK);
      });
    });
  }

  static formatAddressForAutocomplete(address: ParsedAddress): string {
    const parts = [];

    if (address.street_number && address.route) {
      parts.push(`${address.street_number} ${address.route}`);
    } else if (address.route) {
      parts.push(address.route);
    }

    if (address.subpremise) {
      parts.push(address.subpremise);
    }

    if (address.locality) {
      parts.push(address.locality);
    }

    if (address.administrative_area_level_1) {
      parts.push(address.administrative_area_level_1);
    }

    if (address.postal_code) {
      parts.push(address.postal_code);
    }

    if (address.country) {
      parts.push(address.country);
    }

    return parts.join(', ');
  }
}
